import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Wallet, AlertCircle, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { STAGGER_CONTAINER, STAGGER_ITEM, RECHARTS_TOOLTIP_STYLE, RECHARTS_DOT, RECHARTS_ACTIVE_DOT } from '../lib/animations';
import { getAccountIcon } from '../lib/accountIcons';
import { fmtMoney } from '../lib/format';
import { toUIType } from '../lib/liabilityType';
import { maxForecastMonths } from '../lib/tier';
import ExportMenu from '../components/ExportMenu';
import WhatsUnlockedBanner from '../components/WhatsUnlockedBanner';
import SpendingByCategoryWidget from '../components/SpendingByCategoryWidget';
import { Link } from 'react-router-dom';
import { PieChart as PieIcon, Lock } from 'lucide-react';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNT_TYPE_LABEL = {
  current: 'Current',
  checking: 'Current',
  savings: 'Savings',
  credit: 'Credit',
  cash: 'Cash',
  investment: 'Investment',
};

const FREQUENCY_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  every_4_weeks: 'Every 4 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  first_working_day_of_month: '1st working day',
  last_working_day_of_month: 'Last working day',
  'one-off': 'One-off',
};
const formatFrequency = (freq) => FREQUENCY_LABELS[freq] || (freq || '').replace(/_/g, ' ');

const ZERO_LINE_LABEL = { value: '£0', position: 'right', fill: '#94A3B8', fontSize: 11 };

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatMonthLabel = (yyyymm) => {
  if (!yyyymm || typeof yyyymm !== 'string') return yyyymm;
  const [y, m] = yyyymm.split('-');
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return yyyymm;
  return `${MONTH_NAMES[idx]} ${y}`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predictionAccount, setPredictionAccount] = useState('all');
  const [predictionLoading, setPredictionLoading] = useState(false);
  // User can dial the forecast horizon in 6-month chunks up to their tier cap.
  // Stored as a number of months (6, 12, 18, 24, 30, 36). Defaults to the
  // tier's max so paying users see their full window unless they pick less.
  const tierMaxMonths = maxForecastMonths(user);
  const [horizonChoice, setHorizonChoice] = useState(tierMaxMonths);
  // Derive — never write a stale or out-of-tier-range value. If the user
  // downgrades mid-session, the clamp here is the safety net (backend also
  // clamps server-side).
  const predictionHorizon = Math.min(horizonChoice, tierMaxMonths);
  const setPredictionHorizon = setHorizonChoice;

  // Available options: every 6 months up to (and including) the tier cap.
  // Ember -> [6]; Steel -> [6, 12, 18]; Damascus -> [6, 12, 18, 24, 30, 36].
  const horizonOptions = [];
  for (let m = 6; m <= tierMaxMonths; m += 6) horizonOptions.push(m);

  // Subtle upsell chip below the horizon dropdown. Shown when the user is on a
  // tier whose forecast cap is shorter than the next tier up. Hidden for
  // Damascus + admin/staff (already on top tier).
  const horizonUpsell = (() => {
    if (tierMaxMonths >= 36) return null;
    if (tierMaxMonths >= 18) {
      return { copy: 'Want 36-month projections?', cta: 'Forge with Damascus', tier: 'pro_plus' };
    }
    return { copy: 'Want 18-month projections?', cta: 'Forge with Steel', tier: 'pro' };
  })();

  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, typesRes, transactionsRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/accounts`, { withCredentials: true }),
        axios.get(`${API}/account-types`, { withCredentials: true }),
        axios.get(`${API}/transactions`, { withCredentials: true }),
        axios.get(`${API}/predictions/upcoming?days=30`, { withCredentials: true }),
      ]);

      setAccounts(accountsRes.data);
      setTypes(typesRes.data);
      setTransactions(transactionsRes.data.slice(0, 5));
      setUpcoming(upcomingRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPredictions = useCallback(async (accountId, months) => {
    setPredictionLoading(true);
    try {
      // Backend clamps to tier max anyway — frontend just sends the user's pick.
      const params = new URLSearchParams({ months: String(months) });
      if (accountId && accountId !== 'all') params.append('account_id', accountId);
      const res = await axios.get(`${API}/predictions?${params.toString()}`, { withCredentials: true });
      setPredictions(res.data);
    } catch (error) {
      toast.error('Failed to load predictions');
    } finally {
      setPredictionLoading(false);
    }
  }, []);

  /* eslint-disable */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchPredictions(predictionAccount, predictionHorizon);
  }, [fetchPredictions, predictionAccount, predictionHorizon]);

  const typeMap = Object.fromEntries(types.map((t) => [t.slug, t]));
  /* eslint-enable */
  // {account_id: kind} so transaction rows can apply the liability-aware Income/Expense swap.
  const accountKindMap = Object.fromEntries(
    accounts.map((a) => [a.account_id, a.kind || typeMap[a.account_type]?.kind || 'asset'])
  );
  const cashOnHand = accounts.reduce((sum, acc) => {
    const kind = typeMap[acc.account_type]?.kind || 'asset';
    return sum + (kind === 'asset' ? acc.balance : 0);
  }, 0);
  const totalBalance = accounts.reduce((sum, acc) => {
    const kind = typeMap[acc.account_type]?.kind || 'asset';
    const sign = kind === 'liability' ? -1 : 1;
    return sum + acc.balance * sign;
  }, 0);
  // Exclude the first (today's) prediction entry — only count FUTURE months
  const futurePredictions = predictions.slice(1);
  const shortfallMonths = futurePredictions.filter((p) => p.shortfall).length;
  const projectionMonths = futurePredictions.length;
  const estimatedMonths = predictions[0]?.estimated_months_in_window || 0;
  const upcomingEstimates = upcoming.filter((o) => o.is_estimated).length;

  const container = STAGGER_CONTAINER;
  const item = STAGGER_ITEM;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="dashboard-page">
      <motion.div initial="hidden" animate="show" variants={container}>
        <WhatsUnlockedBanner />
        <motion.div variants={item} className="mb-8">
          <PageTitle
            title={`Welcome back, ${user?.name || ''}`}
            subtitle="Here's your financial overview"
            testId="dashboard-title"
            right={
              <ExportMenu
                testIdPrefix="dashboard-export"
                getData={() => {
                  const accountRows = accounts.map((a) => {
                    const t = typeMap[a.account_type];
                    const isLiab = t?.kind === 'liability';
                    return {
                      section: 'Account',
                      label: a.name,
                      detail: t?.name || a.account_type,
                      date: '',
                      amount: (isLiab ? -1 : 1) * a.balance,
                    };
                  });
                  const upcomingRows = upcoming.map((o) => {
                const uiType = toUIType(o.transaction_type, accountKindMap[o.account_id]);
                return {
                  section: 'Upcoming',
                  label: o.description,
                  detail: o.is_estimated ? 'Estimated' : '',
                  date: o.date,
                  amount: uiType === 'income' ? o.amount : -o.amount,
                };
              });
              const predictionRows = predictions.map((p, i) => ({
                section: i === 0 ? 'Today' : 'Forecast',
                label: i === 0 ? 'Current' : `Month +${i}`,
                detail: p.month,
                date: '',
                amount: p.balance,
              }));
              const summaryRows = [
                { section: 'Summary', label: 'Net Worth', detail: `${accounts.length} accounts`, date: '', amount: totalBalance },
                { section: 'Summary', label: 'Cash on Hand', detail: 'assets only', date: '', amount: cashOnHand },
                { section: 'Summary', label: 'Shortfall months', detail: `of next ${projectionMonths}`, date: '', amount: shortfallMonths },
              ];
              return {
                fileName: 'forge-ledger-dashboard',
                sheetName: 'Dashboard',
                columns: [
                  { header: 'Section', key: 'section', width: 12 },
                  { header: 'Label', key: 'label', width: 28 },
                  { header: 'Detail', key: 'detail', width: 22 },
                  { header: 'Date', key: 'date', width: 12 },
                  { header: 'Amount (GBP)', key: 'amount', width: 14 },
                ],
                rows: [...summaryRows, ...accountRows, ...upcomingRows, ...predictionRows],
              };
            }}
          />
            }
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <motion.div
            variants={item}
            className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#CBD5E1] transition-all duration-300"
            data-testid="net-worth-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Overall Net Worth</div>
              <Wallet className="w-5 h-5 text-[#0052FF]" />
            </div>
            <div className={`text-3xl font-bold ${totalBalance < 0 ? 'text-[#EF4444]' : 'text-[#020617]'}`} data-testid="net-worth-value">
              £{fmtMoney(totalBalance)}
            </div>
            <div className="text-sm text-[#64748B] mt-2">across {accounts.length} account{accounts.length === 1 ? '' : 's'}</div>
            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Cash on hand</div>
              <div className="text-xl font-semibold text-[#020617] mt-1" data-testid="cash-on-hand-value">£{fmtMoney(cashOnHand)}</div>
              <div className="text-xs text-[#64748B] mt-1">assets only, debts excluded</div>
            </div>
          </motion.div>

          <motion.div
            variants={item}
            className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6"
            data-testid="account-balances-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Individual Balances</div>
              <Landmark className="w-5 h-5 text-[#0052FF]" />
            </div>
            {accounts.length === 0 ? (
              <p className="text-sm text-[#64748B] py-4">No accounts yet. Add one in the Accounts tab.</p>
            ) : (
              <div className="space-y-2" data-testid="account-balances-list">
                {accounts.map((acc) => {
                  const t = typeMap[acc.account_type];
                  const Icon = getAccountIcon(t?.icon);
                  const isLiability = t?.kind === 'liability';
                  return (
                    <div
                      key={acc.account_id}
                      className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 hover:border-[#CBD5E1] transition-colors gap-3"
                      data-testid={`account-balance-row-${acc.account_id}`}
                    >
                      <div className="min-w-0 flex items-center gap-2 flex-1">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                          isLiability ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#DBEAFE] text-[#1E40AF]'
                        }`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#020617] leading-tight break-words">{acc.name}</div>
                          <div className="text-xs text-[#64748B] leading-tight mt-0.5">{t?.name || ACCOUNT_TYPE_LABEL[acc.account_type] || acc.account_type}</div>
                        </div>
                      </div>
                      <div className={`text-sm font-semibold whitespace-nowrap ${isLiability || acc.balance < 0 ? 'text-[#EF4444]' : 'text-[#020617]'}`}>
                        {isLiability ? '-' : ''}£{fmtMoney(acc.balance)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          <motion.div
            variants={item}
            className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#CBD5E1] transition-all duration-300"
            data-testid="shortfall-warning-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Shortfall Months</div>
              <AlertCircle className={`w-5 h-5 ${shortfallMonths > 0 ? 'text-[#EF4444]' : 'text-[#64748B]'}`} />
            </div>
            <div className={`text-3xl font-bold ${shortfallMonths > 0 ? 'text-[#EF4444]' : 'text-[#020617]'}`} data-testid="shortfall-count">
              {shortfallMonths}
            </div>
            <div className="text-sm text-[#64748B] mt-2">
              of next {projectionMonths} months {predictionAccount !== 'all' ? '(selected)' : '(asset cash)'}
            </div>
            {estimatedMonths > 0 && (
              <div className="text-xs text-[#92400E] bg-[#FEF3C7] rounded px-2 py-1 mt-3 inline-block" data-testid="shortfall-estimated-note">
                {estimatedMonths} of {projectionMonths} months include estimated transactions
              </div>
            )}
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            variants={item}
            className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6"
            data-testid="balance-prediction-chart"
            data-pdf-keep-together="true"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
              <h2 className="text-2xl tracking-tight font-bold text-[#020617]">Future Balance Prediction</h2>
              <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Select value={String(predictionHorizon)} onValueChange={(v) => setPredictionHorizon(parseInt(v, 10))}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="prediction-horizon-select" aria-label="Forecast horizon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {horizonOptions.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          Next {m} months
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={predictionAccount} onValueChange={setPredictionAccount}>
                    <SelectTrigger className="w-full sm:w-56" data-testid="prediction-account-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      {accounts.map((acc) => (
                        <SelectItem key={acc.account_id} value={acc.account_id}>
                          {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {horizonUpsell && (
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#020617] transition-colors"
                    data-testid={`horizon-upsell-${horizonUpsell.tier}`}
                  >
                    <span>{horizonUpsell.copy}</span>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{
                        backgroundImage:
                          horizonUpsell.tier === 'pro_plus'
                            ? 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)'
                            : 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
                      }}
                    >
                      {horizonUpsell.cta} →
                    </span>
                  </Link>
                )}
              </div>
            </div>
            {estimatedMonths > 0 && (
              <div className="text-xs text-[#92400E] bg-[#FEF3C7] rounded px-2 py-1 mb-4 inline-block" data-testid="prediction-estimated-note">
                {estimatedMonths} of {projectionMonths} months include estimated transactions
              </div>
            )}
            {predictionLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={predictions}>
                  <XAxis dataKey="month" tickFormatter={formatMonthLabel} stroke="#64748B" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={RECHARTS_TOOLTIP_STYLE}
                    labelFormatter={formatMonthLabel}
                    formatter={(value) => [`£${fmtMoney(value)}`, 'Balance']}
                  />
                  <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="4 4" label={ZERO_LINE_LABEL} />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke="#0052FF"
                    strokeWidth={2}
                    dot={RECHARTS_DOT}
                    activeDot={RECHARTS_ACTIVE_DOT}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
            <p className="text-xs text-[#64748B] italic mt-4 leading-relaxed" data-testid="forecast-disclaimer">
              Forecasts are only as good as the data you provide. Always ensure you
              input the most accurate future bills, debts, income, etc. to get the
              most accurately <span className="text-[#0052FF] not-italic font-medium">forged</span> financial future possible.
            </p>
          </motion.div>

          <motion.div variants={item} className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="recent-transactions-widget" data-pdf-keep-together="true">
            <h2 className="text-2xl tracking-tight font-bold text-[#020617] mb-6">Recent Transactions</h2>
            <div className="space-y-4">
              {transactions.length === 0 ? (
                <p className="text-sm text-[#64748B] text-center py-8">No transactions yet</p>
              ) : (
                transactions.map((txn) => {
                  const uiType = toUIType(txn.transaction_type, accountKindMap[txn.account_id]);
                  return (
                    <div key={txn.transaction_id} className="flex items-center justify-between pb-4 border-b border-[#E2E8F0] last:border-0" data-testid={`transaction-${txn.transaction_id}`}>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#020617]">{txn.description}</div>
                        <div className="text-xs text-[#64748B] mt-1">{new Date(txn.date).toLocaleDateString('en-GB')}</div>
                      </div>
                      <div className={`text-sm font-semibold ${uiType === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {uiType === 'income' ? '+' : '-'}£{fmtMoney(txn.amount)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>

        <motion.div variants={item} className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="upcoming-occurrences-widget" data-pdf-keep-together="true">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl tracking-tight font-bold text-[#020617]">Upcoming (Next 30 Days)</h2>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]" data-testid="upcoming-count">{upcoming.length} scheduled</span>
          </div>
          {upcomingEstimates > 0 && (
            <div className="text-xs text-[#92400E] bg-[#FEF3C7] rounded px-2 py-1 mb-4 inline-block" data-testid="upcoming-estimated-note">
              {upcomingEstimates} of {upcoming.length} include estimated transactions
            </div>
          )}
          {upcoming.length === 0 ? (
            <p className="text-sm text-[#64748B] text-center py-8">No scheduled occurrences in the next 30 days</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left pb-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Date</th>
                    <th className="text-left pb-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Description</th>
                    <th className="text-left pb-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Frequency</th>
                    <th className="text-left pb-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Category</th>
                    <th className="text-right pb-3 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.slice(0, 10).map((occ, i) => {
                    const uiType = toUIType(occ.transaction_type, accountKindMap[occ.account_id]);
                    return (
                      <tr key={`${occ.recurring_id}-${occ.date}`} className="border-b border-[#E2E8F0] last:border-0" data-testid={`upcoming-row-${i}`}>
                        <td className="py-3 text-sm text-[#334155]">{new Date(occ.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                        <td className="py-3 text-sm font-medium text-[#020617]">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            <span>{occ.description}</span>
                            {occ.is_estimated && (
                              <span className="inline-flex items-center justify-center h-5 px-2 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] leading-none align-middle" title="Estimated amount" data-testid={`upcoming-est-badge-${i}`}>
                                EST
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-[#64748B]">{formatFrequency(occ.frequency)}</td>
                        <td className="py-3 text-sm text-[#64748B]">{occ.category || '-'}</td>
                        <td className={`py-3 text-sm text-right font-semibold ${uiType === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {uiType === 'income' ? '+' : '-'}£{fmtMoney(occ.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {upcoming.length > 10 && (
                <p className="text-xs text-[#64748B] text-center mt-3">+ {upcoming.length - 10} more in the next 30 days</p>
              )}
            </div>
          )}
        </motion.div>

        <motion.div variants={item} className="mt-6">
          {(['pro', 'pro_plus'].includes(user?.tier)) ? (
            <SpendingByCategoryWidget />
          ) : (
            <div
              className="rounded-lg p-[2px]"
              style={{ backgroundImage: 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)' }}
              data-testid="spending-by-category-upsell"
            >
              <div className="bg-white rounded-[8px] p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#F1F5F9] flex items-center justify-center">
                  <PieIcon className="w-6 h-6 text-[#475569]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-bold text-[#020617]">Spending by Category</h2>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundImage: 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)' }}
                    >
                      <Lock className="w-2.5 h-2.5" /> Steel
                    </span>
                  </div>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    See exactly where your money goes each month — and project forward with future-month forecasts based on your recurring transactions. Available on Steel and Damascus.
                  </p>
                </div>
                <Link
                  to="/pricing"
                  className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundImage: 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)' }}
                  data-testid="spending-by-category-upsell-cta"
                >
                  See Steel &amp; Damascus
                </Link>
              </div>
            </div>
          )}
        </motion.div>

        {shortfallMonths > 0 && (
          <motion.div variants={item} className="mt-6 bg-[#FEF2F2] border border-[#EF4444] rounded-lg p-6" data-testid="shortfall-alert">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-[#EF4444] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-[#020617] mb-1">Cash Shortfall Warning</h3>
                <p className="text-sm text-[#64748B]">
                  Your spendable asset balance{predictionAccount !== 'all' ? '' : ' (assets only, mortgages/loans excluded)'} will dip below zero in {shortfallMonths} of the next {projectionMonths} months. Review your recurring transactions to avoid overdraft fees.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
